import type { NotificationDto } from '@shared/api/generated/model'
import {
  deleteAllNotifications,
  deleteNotification,
  getNotifications,
  getUnreadNotificationsCount,
  markAllNotificationsAsRead,
  markNotificationsAsRead,
} from '@shared/api/generated/notifications/notifications'
import {
  normalizePagination,
  requiredString,
  requireEnvelopeData,
  toFiniteNumber,
} from '@shared/lib/api/contract'
import type {
  Notification,
  NotificationFilters,
  NotificationListResponse,
} from '../types/notification.types'
import { NotificationType } from '../types/notification.types'

function mapNotification(notification: NotificationDto): Notification {
  const communicationId = notification.communicationId ?? undefined
  const createdAt = requiredString(notification.createdAt, 'notification.createdAt')

  return {
    id: requiredString(notification.id, 'notification.id'),
    type: communicationId ? NotificationType.COMMUNICATION_PUBLISHED : NotificationType.SYSTEM,
    title: requiredString(notification.title, 'notification.title'),
    message: requiredString(notification.message, 'notification.message'),
    isRead: notification.isRead ?? false,
    createdAt,
    link: communicationId ? `/communications/${communicationId}` : undefined,
  }
}

class NotificationsService {
  async getAll(filters: NotificationFilters = {}): Promise<NotificationListResponse> {
    const [listResponse, unreadResponse] = await Promise.all([
      getNotifications(filters),
      getUnreadNotificationsCount(),
    ])

    return {
      data: (listResponse.data ?? []).map(mapNotification),
      pagination: normalizePagination(listResponse.pagination, {
        page: filters.page ?? 1,
        limit: filters.limit ?? 10,
      }),
      totalCount: toFiniteNumber(
        requireEnvelopeData(unreadResponse, 'contagem de notificações').total,
        toFiniteNumber(listResponse.pagination?.total),
      ),
      unreadCount: toFiniteNumber(
        requireEnvelopeData(unreadResponse, 'contagem de notificações').unread,
      ),
    }
  }

  async markAsRead(notificationIds: string[]): Promise<void> {
    await markNotificationsAsRead({ ids: notificationIds })
  }

  async markAllAsRead(): Promise<void> {
    await markAllNotificationsAsRead()
  }

  async delete(id: string): Promise<void> {
    await deleteNotification(id)
  }

  async deleteAll(): Promise<void> {
    await deleteAllNotifications()
  }

  async getUnreadCount(): Promise<number> {
    const response = await getUnreadNotificationsCount()
    return toFiniteNumber(requireEnvelopeData(response, 'contagem de notificações').unread)
  }
}

export const notificationsService = new NotificationsService()
